import debounce from "lodash/debounce"
import isFunction from "lodash/isFunction"
import get from "lodash/get"
import Checkbox from "../checkbox"
import Empty from "../empty"
import Header from "./header"
import tableMixin from "./table-mixin"
import { getValue } from "./utils"

// 初始化时尽量不让内容换行导致表格每行高度过高
const BIG_COL_WIDTH = 10086

export default {
  name: "mm-tbody",
  components: {
    "ui-checkbox": Checkbox,
    "ui-table-header": Header,
    "ui-empty": Empty,
  },
  mixins: [tableMixin],
  props: {
    height: {
      type: Number,
      default: 0,
    },
    maxHeight: {
      type: Number,
    },
    loading: {
      type: Boolean,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    spanMethod: Function,
    setTableLayout: Function,
    handleClick: Function,
    rowKey: String,
    rowClass: null,
    summary: Boolean,
  },
  inject: {
    rootTable: {
      from: "mm-table",
      default: null,
    },
  },
  computed: {
    wrapperStyle() {
      const {
        scrollBarWidth,
        canScrollX,
        canScrollY,
        headerHeight,
        pageHeight,
        footerHeight,
      } = this.store.layout

      const height = this.height || this.maxHeight
      const result = {
        marginBottom: this.fixed || this.summary || !canScrollX ? `-${scrollBarWidth}px` : 0,
        marginRight:
          (this.fixed && this.fixed === "left") || !canScrollY ? `-${scrollBarWidth}px` : 0,
      }
      const resultHeight = `${height - headerHeight - footerHeight - pageHeight + (canScrollX ? 0 : scrollBarWidth)}px`

      if (this.height) {
        return {
          ...result,
          height: resultHeight,
        }
      } else if (this.maxHeight) {
        return {
          ...result,
          maxHeight: resultHeight,
        }
      }

      return result
    },
    debounceResizeHandler() {
      return debounce(this.resizeHandler, 60)
    },
  },
  watch: {
    columns: {
      immediate: true,
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (this.$isServer || !this.$el) {
            return
          }
          this.tableLayout()
          if (!this.fixed) {
            this.setRowHeightList()
          }

          this.$el.scrollTop = 0
        })
      },
    },
    "store.state.filteredRows"() {
      if (this.$isServer || !this.$el) {
        return
      }
      this.$nextTick(() => {
        this.tableLayout()
      })
      if (!this.fixed) {
        this.setRowHeightList()
      }
    },
  },
  mounted() {
    const refKey = !this.fixed ? "tbody" : this.fixed === "right" ? "rightFixedTbody" : "fixedTbody"
    this.store.setRef(refKey, this.$el)
    if (!this.fixed) {
      window.addEventListener("resize", this.debounceResizeHandler)
    }
  },
  beforeDestroy() {
    if (!this.fixed) {
      window.removeEventListener("resize", this.debounceResizeHandler)
    }
  },
  methods: {
    resizeHandler() {
      this.setRowHeightList()
      this.tableLayout()
    },
    setRowHeightList() {
      const { tbody } = this.$refs
      this.$nextTick(() => {
        if (tbody) {
          this.store.setLayout("rowHeightList", Array.from(tbody.children, tr => tr.offsetHeight))
        }
      })
    },
    tableLayout() {
      const { tbody } = this.$refs
      if (tbody && this.setTableLayout && !this.fixed) {
        this.setTableLayout()
      }
    },
    getCheckboxProps(row) {
      const result =
        this.rowSelection && this.rowSelection.getCheckboxProps
          ? this.rowSelection.getCheckboxProps(row)
          : {}
      return result
    },
    mouseoverHandler(e) {
      let { target, currentTarget } = e
      while (target !== currentTarget && target.tagName !== "TR") {
        target = target.parentNode
      }
      const index = target.rowIndex - (this.height || this.maxHeight ? 0 : 1)
      if (target.tagName === "TR" && index !== this.hoverRowIndex) {
        this.store.setLayout("hoverRowIndex", index)
      }
    },
    mouseleaveHandler() {
      this.store.setLayout("hoverRowIndex", -1)
    },
    handleDragstart(row) {
      if (this.draggable) {
        this.store.setDragRow(row)
      }
    },
    handleDragenter(row, rowIndex) {
      if (this.draggable && this.store.state.dragRow) {
        this.store.setDragoverIndex(row, rowIndex)
      }
    },
    handleDrop(row) {
      if (this.draggable && this.store.state.dragRow) {
        this.store.setDrop(row, (newIndex, oldIndex) => {
          this.$emit("drop", newIndex, oldIndex)
        })
      }
    },
    handleDragend() {
      if (this.draggable) {
        this.store.setDragleave()
      }
    },
    isEmptyValue(value) {
      return (typeof value === "string" && !value) || value === undefined || value === null
    },
  },
  render(h) {
    const { layout, state } = this.store
    const { rowKey, rowClass, tableStyle } = this

    return (
      <div class="mm-tbody" style={this.wrapperStyle} onScroll={this.scrollHandler}>
        <table cellpadding="0" cellspacing="0" border="0" style={tableStyle}>
          <colgroup>
            {this.showSelectCol && <col width={layout.selectColWidth} />}
            {this.flattenColumns.map((column, index) => (
              <col key={index} width={column.width || BIG_COL_WIDTH} />
            ))}
          </colgroup>
          {!this.height && !this.maxHeight && (
            <ui-table-header
              columns={this.columns}
              fixed={this.fixed}
              store={this.store}
              rowSelection={this.rowSelection}
              showSelectColumn={this.showSelectCol}
            />
          )}
          <tbody
            ref="tbody"
            onMouseover={this.mouseoverHandler}
            onMouseleave={this.mouseleaveHandler}
          >
            {state.filteredRows.length ? (
              state.filteredRows.map((row, rowIndex) => (
                <tr
                  key={rowKey ? get(row, rowKey) : rowIndex}
                  class={[
                    {
                      "mm-tbody-tr--active": rowIndex === layout.hoverRowIndex,
                      "mm-tbody-tr--selected": state.selectedRows.includes(
                        rowKey ? get(row, rowKey) : row
                      ),
                      "mm-tbody-tr--dragover-up":
                        rowIndex === layout.dragoverRowIndex && !layout.dragDirection,
                      "mm-tbody-tr--dragover-down":
                        rowIndex === layout.dragoverRowIndex && layout.dragDirection,
                    },
                    isFunction(rowClass) ? rowClass({ row, rowIndex, rowKey }) : rowClass,
                  ]}
                  style={{ height: this.fixed ? `${layout.rowHeightList[rowIndex]}px` : "" }}
                  draggable={this.draggable}
                  onDragstart={() => {
                    this.handleDragstart(row)
                  }}
                  onDragend={this.handleDragend}
                  onDragenter={() => {
                    this.handleDragenter(row, rowIndex)
                  }}
                  onDragover={e => e.preventDefault()}
                  onDrop={() => {
                    this.handleDrop(row)
                  }}
                >
                  {this.showSelectCol && (
                    <td class="mm-table-selection-col">
                      <div class="mm-table-cell">
                        <ui-checkbox
                          checked={state.selectedRows}
                          value={rowKey ? get(row, rowKey) : row}
                          onInput={val => (state.selectedRows = val)}
                          {...{ props: this.getCheckboxProps(row) }}
                        />
                      </div>
                    </td>
                  )}
                  {this.flattenColumns.map((column, colIndex) => {
                    const columnData = {
                      column,
                      row,
                      rowIndex,
                      colIndex,
                      key: column.key,
                      value: getValue({ row, column }),
                    }
                    return (
                      <td
                        key={colIndex}
                        {...{
                          attrs: this.spanMethod
                            ? this.spanMethod({ row, column, rowIndex, colIndex })
                            : {},
                        }}
                        onClick={e => {
                          this.handleClick(e, { row, rowIndex, colIndex })
                        }}
                      >
                        <div
                          class={[
                            "mm-table-cell",
                            {
                              [column.className]: column.className,
                              "mm-table-cell--ellipsis": column.ellipsis,
                            },
                          ]}
                          style={{
                            textAlign: column.align || "left",
                            ...(column.style || {}),
                          }}
                          title={!column.render && column.ellipsis ? columnData.value : ""}
                        >
                          {column.render
                            ? column.render(h, columnData)
                            : this.rootTable.$scopedSlots[column.slot]
                              ? this.rootTable.$scopedSlots[column.slot](columnData)
                              : this.isEmptyValue(columnData.value)
                                ? column.placeholder || ""
                                : columnData.value}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))
            ) : this.fixed ? null : (
              <tr>
                <td
                  class="empty-placeholder"
                  colspan={this.flattenColumns.length + (this.showSelectCol ? 1 : 0)}
                />
              </tr>
            )}
          </tbody>
        </table>
        { !this.loading && !this.fixed && !state.filteredRows.length && (
          <div class="mm-empty-no-data">
            <ui-empty type="small"/>
          </div>
        )}
      </div>
    )
  },
}
